export const RESULT_PAGE_SIZE = 12;

export const SORT_OPTIONS = [
  { value: "smrt", labelKey: "smrtSearch.sortSmrtIndex" },
  { value: "goals", labelKey: "smrtSearch.sortGoals" },
  { value: "assists", labelKey: "smrtSearch.sortAssists" },
  { value: "xg", labelKey: "smrtSearch.sortXg" },
  { value: "age", labelKey: "smrtSearch.sortAge" },
];

export const VIEW_MODES = {
  GRID: "grid",
  LIST: "list",
};

export const STAT_MODES = {
  SEASON: "season",
  MATCH: "match",
  NINETY: "90",
};

export const DEFAULT_STAT_MODE = STAT_MODES.SEASON;

export const QUICK_FILTER_LABELS = [
  "GK",
  "DEF",
  "MID",
  "FWD",
  "Left foot",
  "Right foot",
  "Male",
  "Female",
];

export const ADVANCED_PARAM_QUERY_PREFIX = "param_";

export const VISIBLE_FILTER_KEYS = [
  "selectedCompetition",
  "selectedSeason",
  "selectedNation",
  "selectedPosition",
  "selectedTeamType",
  "selectedGender",
  "selectedLeg",
  "olderAge",
  "underAge",
  "heightFrom",
  "heightTo",
  "xgFrom",
  "xgTo",
  "npxgFrom",
  "npxgTo",
  "xaFrom",
  "xaTo",
  "smrtIndexLeagueFrom",
  "smrtIndexLeagueTo",
  "smrtIndexInternationalFrom",
  "smrtIndexInternationalTo",
  "goalsFrom",
  "goalsTo",
];

const ARRAY_FILTER_KEYS = new Set([
  "selectedCompetition",
  "selectedSeason",
  "selectedNation",
  "selectedPosition",
]);

const SEARCH_SCOPE_FILTER_KEYS = [
  "selectedCompetition",
  "selectedSeason",
  "selectedNation",
  "selectedPosition",
  "selectedTeamType",
  "olderAge",
  "underAge",
  "heightFrom",
  "heightTo",
  "xgFrom",
  "xgTo",
  "npxgFrom",
  "npxgTo",
  "xaFrom",
  "xaTo",
  "smrtIndexLeagueFrom",
  "smrtIndexLeagueTo",
  "smrtIndexInternationalFrom",
  "smrtIndexInternationalTo",
  "goalsFrom",
  "goalsTo",
];

const QUICK_POSITION_KEYS = new Set(["GK", "DEF", "MID", "FWD"]);
const POSITION_CODE_PATTERN = /^[A-Z0-9]{2,6}$/;
const STAT_MODE_PRIORITY = {
  [STAT_MODES.SEASON]: ["main", "total", "count", "value", "avg_matches", "avg_90"],
  [STAT_MODES.MATCH]: ["avg_matches", "main", "total", "count", "value", "avg_90"],
  [STAT_MODES.NINETY]: ["avg_90", "avg_matches", "main", "total", "count", "value"],
};
const SORT_FIELD_BY_KEY = {
  smrt: "206",
  goals: "2",
  assists: "201",
};

const CHIP_LABEL_DEFAULTS = {
  competition: "Competition",
  season: "Season",
  nationality: "Nationality",
  leg: "Leg",
  both: "Both",
  age: "Age",
  height: "Height",
  teamType: "Team type",
  xg: "xG",
  npxg: "NPxG",
  xa: "xA",
  smrtIndexLeague: "SMRT Index League",
  smrtIndexInternational: "SMRT Index International",
  goals: "Goals",
};

const POSITION_LABEL_MATCHERS = {
  GK: ["gk", "goalkeeper", "keeper"],
  DEF: ["def", "defender", "centreback", "centerback", "fullback", "back"],
  MID: ["mid", "midfielder", "midfield"],
  FWD: ["fwd", "forward", "attacker", "striker", "winger"],
};

const SAFE_NUMBER_FALLBACK = 0;
const DEFAULT_RESULT_PARAM_IDS = ["203", "206", "207"];

export const getDefaultFilters = () => ({
  selectedCompetition: [],
  selectedSeason: [],
  selectedNation: [],
  selectedPosition: [],
  selectedTeamType: "",
  selectedGender: "",
  selectedLeg: "",
  olderAge: "",
  underAge: "",
  heightFrom: "",
  heightTo: "",
  xgFrom: "",
  xgTo: "",
  npxgFrom: "",
  npxgTo: "",
  xaFrom: "",
  xaTo: "",
  smrtIndexLeagueFrom: "",
  smrtIndexLeagueTo: "",
  smrtIndexInternationalFrom: "",
  smrtIndexInternationalTo: "",
  goalsFrom: "",
  goalsTo: "",
  advancedParamRanges: {},
});

export const getDefaultSearchState = () => ({
  filters: getDefaultFilters(),
  page: 1,
  sort: "smrt",
  statMode: DEFAULT_STAT_MODE,
});

export const normalizeSearchStatMode = (value) => {
  if (value === STAT_MODES.MATCH || value === STAT_MODES.NINETY) {
    return value;
  }

  return DEFAULT_STAT_MODE;
};

export const normalizeSearchSortKey = (value) =>
  SORT_OPTIONS.some((option) => option.value === value)
    ? value
    : getDefaultSearchState().sort;

const hasRangeValue = (range = {}) => Boolean(range?.from || range?.to);

const normalizeIdArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item))
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeScalarValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
};

export const normalizeAdvancedParamRanges = (
  value,
  { legacyStatMode = DEFAULT_STAT_MODE } = {}
) => {
  if (!value || typeof value !== "object") {
    return {};
  }

  const normalizedLegacyMode = normalizeSearchStatMode(legacyStatMode);

  return Object.entries(value).reduce((acc, [paramId, rangeValue]) => {
    const normalizedParamId = String(paramId).trim();
    const parsedParamId = Number(normalizedParamId);

    if (!Number.isInteger(parsedParamId) || parsedParamId <= 0) {
      return acc;
    }

    const candidateRange =
      rangeValue && typeof rangeValue === "object" ? rangeValue : {};
    const hasExplicitMode = Object.prototype.hasOwnProperty.call(
      candidateRange,
      "mode"
    );
    const normalizedFrom = normalizeScalarValue(candidateRange.from);
    const normalizedTo = normalizeScalarValue(candidateRange.to);
    const hasBounds = Boolean(normalizedFrom || normalizedTo);
    const normalizedMode = hasExplicitMode
      ? normalizeSearchStatMode(candidateRange.mode)
      : hasBounds
        ? normalizedLegacyMode
        : DEFAULT_STAT_MODE;
    const normalizedRange = {
      from: normalizedFrom,
      to: normalizedTo,
      mode: normalizedMode,
    };

    if (!hasBounds && normalizedRange.mode === DEFAULT_STAT_MODE) {
      return acc;
    }

    acc[String(parsedParamId)] = normalizedRange;
    return acc;
  }, {});
};

export const normalizeFilters = (value) => {
  const defaults = getDefaultFilters();
  const candidate = value && typeof value === "object" ? value : {};
  const normalizedFilters = VISIBLE_FILTER_KEYS.reduce((acc, key) => {
    const nextValue = candidate[key];

    if (ARRAY_FILTER_KEYS.has(key)) {
      acc[key] = normalizeIdArray(nextValue);
      return acc;
    }

    acc[key] = normalizeScalarValue(nextValue);
    return acc;
  }, {});

  return {
    ...defaults,
    ...normalizedFilters,
    advancedParamRanges: normalizeAdvancedParamRanges(candidate.advancedParamRanges),
  };
};

export const normalizeMetricLabel = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const getAdvancedParamRawLabel = (parameter = {}) =>
  String(parameter?.name || parameter?.parameter_name || "").trim();

const getAdvancedParamFallbackLabel = (parameter = {}) => {
  const rawLabel = getAdvancedParamRawLabel(parameter);

  if (rawLabel) {
    return rawLabel;
  }

  const parameterId = Number(parameter?.id ?? parameter?.parameter_id);
  return Number.isFinite(parameterId) ? `Parameter ${parameterId}` : "Unnamed parameter";
};

const getAdvancedParamFeaturedKey = (parameter = {}) => {
  const parameterId = Number(parameter?.id ?? parameter?.parameter_id);
  const normalizedRawLabel = normalizeMetricLabel(getAdvancedParamRawLabel(parameter));

  if (normalizedRawLabel === "xg") {
    return "xg";
  }

  if (normalizedRawLabel === "npxg") {
    return "npxg";
  }

  if (normalizedRawLabel === "xa") {
    return "xa";
  }

  if (parameterId === 206 || normalizedRawLabel === "smrtindexleague") {
    return "smrtindexleague";
  }

  if (
    parameterId === 207 ||
    normalizedRawLabel === "smrtindexinternational"
  ) {
    return "smrtindexinternational";
  }

  if (parameterId === 2 || normalizedRawLabel === "goals") {
    return "goals";
  }

  return "";
};

export const normalizePositionKey = (value = "") => {
  const normalizedValue = normalizeMetricLabel(value);

  if (!normalizedValue) {
    return "";
  }

  const matchedEntry = Object.entries(POSITION_LABEL_MATCHERS).find(([, matchers]) =>
    matchers.some((matcher) => normalizedValue.includes(matcher))
  );

  return matchedEntry?.[0] || normalizedValue.toUpperCase().slice(0, 3);
};

export const getPositionOptionIdByCode = (positionOptions = [], code) => {
  const matchedPosition = positionOptions.find((position) => {
    const positionName = position?.name || position?.label || "";
    return normalizePositionKey(positionName) === code;
  });

  return matchedPosition ? String(matchedPosition.id) : "";
};

export const getQuickFilterState = (filters) => {
  const nextFilters = normalizeFilters(filters);
  const quickFilters = [];

  if (nextFilters.selectedLeg === "L") {
    quickFilters.push("Left foot");
  }

  if (nextFilters.selectedLeg === "R") {
    quickFilters.push("Right foot");
  }

  if (nextFilters.selectedGender === "M") {
    quickFilters.push("Male");
  }

  if (nextFilters.selectedGender === "F") {
    quickFilters.push("Female");
  }

  return QUICK_FILTER_LABELS.filter((label) => quickFilters.includes(label));
};

export const normalizeNextPageUrl = (url) => {
  if (!url) {
    return null;
  }

  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.pathname}${parsedUrl.search}`;
  } catch (error) {
    return url;
  }
};

export const loadAllPlayerSearchParameters = async (
  requestPage,
  initialPage = "/stats/parameters/player/"
) => {
  if (typeof requestPage !== "function") {
    return [];
  }

  let nextPage = initialPage;
  const collectedParameters = [];

  while (nextPage) {
    const response = await requestPage(nextPage);
    const payload = response?.data;

    if (Array.isArray(payload)) {
      collectedParameters.push(...payload);
      break;
    }

    const pageResults = Array.isArray(payload?.results) ? payload.results : [];
    collectedParameters.push(...pageResults);

    nextPage = payload?.next ? normalizeNextPageUrl(payload.next) : null;
  }

  return collectedParameters;
};

export const toggleQuickFilter = (filters, label, positionOptions = []) => {
  const nextFilters = normalizeFilters(filters);

  if (QUICK_POSITION_KEYS.has(label)) {
    const positionId = getPositionOptionIdByCode(positionOptions, label);

    if (!positionId) {
      return nextFilters;
    }

    const isSelected = nextFilters.selectedPosition.includes(positionId);

    return {
      ...nextFilters,
      selectedPosition: isSelected
        ? nextFilters.selectedPosition.filter((id) => id !== positionId)
        : [...nextFilters.selectedPosition, positionId],
    };
  }

  if (label === "Left foot") {
    return {
      ...nextFilters,
      selectedLeg: nextFilters.selectedLeg === "L" ? "" : "L",
    };
  }

  if (label === "Right foot") {
    return {
      ...nextFilters,
      selectedLeg: nextFilters.selectedLeg === "R" ? "" : "R",
    };
  }

  if (label === "Male") {
    return {
      ...nextFilters,
      selectedGender: nextFilters.selectedGender === "M" ? "" : "M",
    };
  }

  if (label === "Female") {
    return {
      ...nextFilters,
      selectedGender: nextFilters.selectedGender === "F" ? "" : "F",
    };
  }

  return nextFilters;
};

const normalizeParameterId = (value) => {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const sortParameterIds = (values = []) =>
  Array.from(new Set(values))
    .filter((value) => Number.isInteger(value) && value > 0)
    .sort((leftValue, rightValue) => leftValue - rightValue);

const normalizeTemplateRangeCollection = (
  value,
  { legacyStatMode = DEFAULT_STAT_MODE } = {}
) => {
  if (Array.isArray(value)) {
    const rangeMap = value.reduce((acc, item) => {
      const parameterId = normalizeParameterId(
        item?.id ?? item?.paramId ?? item?.parameterId ?? item?.parameter_id
      );

      if (!parameterId) {
        return acc;
      }

      acc[parameterId] = {
        from: item?.from,
        to: item?.to,
        mode: item?.mode,
      };
      return acc;
    }, {});

    return normalizeAdvancedParamRanges(rangeMap, { legacyStatMode });
  }

  return normalizeAdvancedParamRanges(value, { legacyStatMode });
};

const hasTemplateParameterRanges = (state) =>
  Boolean(
    state &&
      typeof state === "object" &&
      Object.prototype.hasOwnProperty.call(state, "parameterRanges")
  );

const getCanonicalTemplateRanges = (
  state,
  { legacyStatMode = DEFAULT_STAT_MODE } = {}
) => {
  const stateObject = state && typeof state === "object" ? state : {};
  const rangeSource = hasTemplateParameterRanges(stateObject)
    ? stateObject.parameterRanges
    : stateObject.advancedParamRanges;

  return normalizeTemplateRangeCollection(rangeSource, { legacyStatMode });
};

const buildTemplateAdvancedParamRanges = (
  state,
  {
    advancedParameters = [],
    featuredAdvancedFieldMap = {},
    legacyStatMode = DEFAULT_STAT_MODE,
  } = {}
) => {
  const stateObject = state && typeof state === "object" ? state : {};
  const nextRanges = getCanonicalTemplateRanges(stateObject, {
    legacyStatMode,
  });

  advancedParameters.forEach((parameter) => {
    const parameterId = normalizeParameterId(
      parameter?.id ?? parameter?.parameter_id
    );
    const staticFieldKeys =
      featuredAdvancedFieldMap?.[parameter?.featuredKey];

    if (!parameterId || !staticFieldKeys) {
      return;
    }

    const parameterIdKey = String(parameterId);
    const savedRange = nextRanges[parameterIdKey];

    if (!savedRange) {
      return;
    }

    const savedMode = normalizeSearchStatMode(savedRange.mode);

    if (savedMode === DEFAULT_STAT_MODE) {
      delete nextRanges[parameterIdKey];
      return;
    }

    nextRanges[parameterIdKey] = {
      from: "",
      to: "",
      mode: savedMode,
    };
  });

  return normalizeAdvancedParamRanges(nextRanges, { legacyStatMode });
};

const buildFeaturedFilterFieldsFromTemplateRanges = (
  state,
  {
    advancedParameters = [],
    featuredAdvancedFieldMap = {},
    legacyStatMode = DEFAULT_STAT_MODE,
  } = {}
) => {
  const stateObject = state && typeof state === "object" ? state : {};
  const savedRanges = getCanonicalTemplateRanges(stateObject, {
    legacyStatMode,
  });

  return advancedParameters.reduce((acc, parameter) => {
    const parameterId = normalizeParameterId(
      parameter?.id ?? parameter?.parameter_id
    );
    const staticFieldKeys =
      featuredAdvancedFieldMap?.[parameter?.featuredKey];

    if (!parameterId || !staticFieldKeys) {
      return acc;
    }

    const [fromField, toField] = staticFieldKeys;
    const savedRange = savedRanges[String(parameterId)];

    if (!savedRange || (!savedRange.from && !savedRange.to)) {
      return acc;
    }

    if (!stateObject[fromField]) {
      acc[fromField] = savedRange.from;
    }

    if (!stateObject[toField]) {
      acc[toField] = savedRange.to;
    }

    return acc;
  }, {});
};

export const buildTemplateParameterSnapshot = (
  filters,
  {
    advancedParameters = [],
    featuredAdvancedFieldMap = {},
  } = {}
) => {
  const nextFilters = normalizeFilters(filters);
  const advancedParamRanges = normalizeAdvancedParamRanges(
    nextFilters.advancedParamRanges
  );
  const parameterIds = new Set(
    getSelectedAdvancedParamIds(advancedParamRanges)
      .map((paramId) => normalizeParameterId(paramId))
      .filter((paramId) => paramId !== null)
  );
  const parameterRanges = {};

  Object.entries(advancedParamRanges).forEach(([paramId, range]) => {
    if (!hasRangeValue(range)) {
      return;
    }

    parameterRanges[paramId] = {
      from: range.from,
      to: range.to,
      mode: normalizeSearchStatMode(range.mode),
    };
  });

  advancedParameters.forEach((parameter) => {
    const parameterId = normalizeParameterId(
      parameter?.id ?? parameter?.parameter_id
    );
    const staticFieldKeys =
      featuredAdvancedFieldMap?.[parameter?.featuredKey];

    if (!parameterId || !staticFieldKeys) {
      return;
    }

    const [fromField, toField] = staticFieldKeys;
    const from = normalizeScalarValue(nextFilters[fromField]);
    const to = normalizeScalarValue(nextFilters[toField]);

    if (!from && !to) {
      return;
    }

    const parameterIdKey = String(parameterId);
    const mode = normalizeSearchStatMode(
      advancedParamRanges[parameterIdKey]?.mode
    );

    parameterIds.add(parameterId);
    parameterRanges[parameterIdKey] = {
      from,
      to,
      mode,
    };
  });

  const sortedParameterIds = sortParameterIds(Array.from(parameterIds));
  const parameterModes = sortedParameterIds.reduce((acc, parameterId) => {
    const parameterIdKey = String(parameterId);
    const range = parameterRanges[parameterIdKey];

    if (range) {
      acc[parameterIdKey] = normalizeSearchStatMode(range.mode);
    }

    return acc;
  }, {});

  // Persist the saved ranges as a list of `{ id, from, to, mode }` records
  // instead of an object keyed by parameter id. A list of uniform objects
  // survives strict template-storage backends that drop dynamically keyed
  // maps, and the read path (normalizeTemplateRangeCollection) already
  // accepts both shapes for backward compatibility with older templates.
  const parameterRangeList = sortedParameterIds.reduce((acc, parameterId) => {
    const range = parameterRanges[String(parameterId)];

    if (range) {
      acc.push({
        id: parameterId,
        from: range.from,
        to: range.to,
        mode: normalizeSearchStatMode(range.mode),
      });
    }

    return acc;
  }, []);

  return {
    parameterIds: sortedParameterIds,
    parameterModes,
    parameterRanges: parameterRangeList,
  };
};

export const captureState = (
  filters,
  positionOptions = [],
  {
    advancedParameters = [],
    featuredAdvancedFieldMap = {},
    sort,
  } = {}
) => {
  const nextFilters = normalizeFilters(filters);

  return {
    qfs: getQuickFilterState(nextFilters, positionOptions),
    sort: normalizeSearchSortKey(sort),
    selectedCompetition: [...nextFilters.selectedCompetition],
    selectedSeason: [...nextFilters.selectedSeason],
    selectedNation: [...nextFilters.selectedNation],
    selectedPosition: [...nextFilters.selectedPosition],
    selectedTeamType: nextFilters.selectedTeamType,
    selectedGender: nextFilters.selectedGender,
    selectedLeg: nextFilters.selectedLeg,
    olderAge: nextFilters.olderAge,
    underAge: nextFilters.underAge,
    heightFrom: nextFilters.heightFrom,
    heightTo: nextFilters.heightTo,
    xgFrom: nextFilters.xgFrom,
    xgTo: nextFilters.xgTo,
    npxgFrom: nextFilters.npxgFrom,
    npxgTo: nextFilters.npxgTo,
    xaFrom: nextFilters.xaFrom,
    xaTo: nextFilters.xaTo,
    smrtIndexLeagueFrom: nextFilters.smrtIndexLeagueFrom,
    smrtIndexLeagueTo: nextFilters.smrtIndexLeagueTo,
    smrtIndexInternationalFrom: nextFilters.smrtIndexInternationalFrom,
    smrtIndexInternationalTo: nextFilters.smrtIndexInternationalTo,
    goalsFrom: nextFilters.goalsFrom,
    goalsTo: nextFilters.goalsTo,
    advancedParamRanges: normalizeAdvancedParamRanges(
      nextFilters.advancedParamRanges
    ),
    ...buildTemplateParameterSnapshot(nextFilters, {
      advancedParameters,
      featuredAdvancedFieldMap,
    }),
  };
};

export const saveTemplateLocally = ({
  templates = [],
  name = "",
  state = {},
  storageKey = "smrtTemplates",
} = {}) => {
  const normalizedName = String(name || "").trim();

  if (!normalizedName) {
    return Array.isArray(templates) ? templates : [];
  }

  const nextTemplate = {
    name: normalizedName,
    state,
  };
  const nextTemplates = (Array.isArray(templates) ? templates : [])
    .filter(
      (template) =>
        String(template?.name || "").trim().toLowerCase() !==
        normalizedName.toLowerCase()
    )
    .concat(nextTemplate);

  localStorage.setItem(storageKey, JSON.stringify(nextTemplates));
  return nextTemplates;
};

export const getTemplateStateStatMode = (state) =>
  normalizeSearchStatMode(state?.statMode);

export const applyState = (
  state,
  positionOptions = [],
  {
    advancedParameters = [],
    featuredAdvancedFieldMap = {},
  } = {}
) => {
  const legacyStatMode = getTemplateStateStatMode(state);
  const templateAdvancedParamRanges = buildTemplateAdvancedParamRanges(state, {
    advancedParameters,
    featuredAdvancedFieldMap,
    legacyStatMode,
  });
  const featuredFilterFields = buildFeaturedFilterFieldsFromTemplateRanges(
    state,
    {
      advancedParameters,
      featuredAdvancedFieldMap,
      legacyStatMode,
    }
  );
  const nextFilters = normalizeFilters({
    ...(state && typeof state === "object" ? state : {}),
    ...featuredFilterFields,
    advancedParamRanges: templateAdvancedParamRanges,
  });
  const quickFilters = Array.isArray(state?.qfs)
    ? state.qfs.filter((label) => QUICK_FILTER_LABELS.includes(label))
    : [];
  const hasExplicitSelectedPosition =
    state &&
    typeof state === "object" &&
    Object.prototype.hasOwnProperty.call(state, "selectedPosition");

  if (quickFilters.length === 0) {
    return nextFilters;
  }

  const nextPositionIds = quickFilters
    .filter((label) => QUICK_POSITION_KEYS.has(label))
    .map((label) => getPositionOptionIdByCode(positionOptions, label))
    .filter(Boolean);

  const hasFootQuickFilter = quickFilters.some(
    (label) => label === "Left foot" || label === "Right foot"
  );
  const hasGenderQuickFilter = quickFilters.some(
    (label) => label === "Male" || label === "Female"
  );

  return {
    ...nextFilters,
    selectedPosition:
      hasExplicitSelectedPosition
        ? nextFilters.selectedPosition
        : nextPositionIds.length > 0
          ? nextPositionIds
          : nextFilters.selectedPosition,
    selectedLeg: hasFootQuickFilter
      ? quickFilters.includes("Left foot")
        ? "L"
        : quickFilters.includes("Right foot")
          ? "R"
          : ""
      : nextFilters.selectedLeg,
    selectedGender: hasGenderQuickFilter
      ? quickFilters.includes("Male")
        ? "M"
        : quickFilters.includes("Female")
          ? "F"
          : ""
      : nextFilters.selectedGender,
    advancedParamRanges: normalizeAdvancedParamRanges(
      nextFilters.advancedParamRanges,
      {
        legacyStatMode,
      }
    ),
  };
};

const formatRangeValue = (from, to) => {
  if (from && to) {
    return `${from}-${to}`;
  }

  if (from) {
    return `>= ${from}`;
  }

  if (to) {
    return `<= ${to}`;
  }

  return "";
};

const normalizePositionDisplayValue = (value = "") => String(value || "").trim();

const normalizeTrustedPositionCode = (value = "") => {
  const normalizedValue = normalizePositionDisplayValue(value);
  const normalizedUpper = normalizedValue.toUpperCase();
  return POSITION_CODE_PATTERN.test(normalizedUpper) ? normalizedUpper : "";
};

const normalizePositionDescriptor = (value = "") =>
  normalizePositionDisplayValue(value)
    .toLowerCase()
    .replace(/[_/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const includesAnyPositionToken = (text, tokens = []) =>
  tokens.some((token) => text.includes(token));

const resolvePositionRoleCode = (descriptor = "") => {
  if (
    includesAnyPositionToken(descriptor, [
      "goalkeeper",
      "goal keeper",
    ])
  ) {
    return "GK";
  }

  if (includesAnyPositionToken(descriptor, ["attacking midfielder"])) {
    return "AM";
  }

  if (
    includesAnyPositionToken(descriptor, [
      "defensive midfielder",
      "holding midfielder",
    ])
  ) {
    return "DM";
  }

  if (includesAnyPositionToken(descriptor, ["midfielder", "midfield"])) {
    return "M";
  }

  if (
    includesAnyPositionToken(descriptor, [
      "defender",
      "center back",
      "centre back",
      "center-back",
      "centre-back",
      "fullback",
      "wingback",
      "wing back",
      "back",
    ])
  ) {
    return "D";
  }

  if (
    includesAnyPositionToken(descriptor, [
      "forward",
      "attacker",
      "striker",
      "winger",
    ])
  ) {
    return "F";
  }

  return "";
};

const resolvePositionSideCode = (descriptor = "") => {
  if (
    includesAnyPositionToken(descriptor, ["left central", "left centre"])
  ) {
    return "LC";
  }

  if (
    includesAnyPositionToken(descriptor, ["right central", "right centre"])
  ) {
    return "RC";
  }

  if (includesAnyPositionToken(descriptor, ["central", "center", "centre"])) {
    return "C";
  }

  if (descriptor.includes("left")) {
    return "L";
  }

  if (descriptor.includes("right")) {
    return "R";
  }

  return "";
};

const derivePositionCodeFromDescriptor = (value = "") => {
  const descriptor = normalizePositionDescriptor(value);

  if (!descriptor) {
    return "";
  }

  const roleCode = resolvePositionRoleCode(descriptor);

  if (!roleCode) {
    return "";
  }

  if (roleCode === "GK") {
    return "GK";
  }

  const sideCode = resolvePositionSideCode(descriptor);

  if (sideCode) {
    return `${sideCode}${roleCode}`;
  }

  if (roleCode === "M") {
    return "MID";
  }

  if (roleCode === "D") {
    return "DEF";
  }

  if (roleCode === "F") {
    return "FWD";
  }

  return roleCode;
};

const normalizeFallbackPositionCode = (value = "") => {
  const normalizedValue = normalizePositionDisplayValue(value);
  const normalizedUpper = normalizedValue.toUpperCase();

  if (
    normalizedValue &&
    normalizedValue === normalizedUpper &&
    POSITION_CODE_PATTERN.test(normalizedUpper)
  ) {
    return normalizedUpper;
  }

  return "";
};

const getPositionDisplayLabel = (position = {}) => {
  const trustedCandidates = [
    position?.short_name,
    position?.shortName,
    position?.code,
  ]
    .map((value) => normalizeTrustedPositionCode(value))
    .filter(Boolean);
  const trustedCodeCandidate = trustedCandidates[0];

  if (trustedCodeCandidate) {
    return trustedCodeCandidate;
  }

  const derivedCodeCandidate = [position?.name, position?.label]
    .map((value) => derivePositionCodeFromDescriptor(value))
    .find(Boolean);

  if (derivedCodeCandidate) {
    return derivedCodeCandidate;
  }

  const fallbackCandidates = [position?.name, position?.label]
    .map((value) => normalizePositionDisplayValue(value))
    .filter(Boolean);
  const fallbackCodeCandidate = fallbackCandidates
    .map((value) => normalizeFallbackPositionCode(value))
    .find(Boolean);

  if (fallbackCodeCandidate) {
    return fallbackCodeCandidate;
  }

  return fallbackCandidates[0] || "";
};

const getOptionLabel = (
  options = [],
  id,
  { preferYears = false, preferPositionCode = false } = {}
) => {
  const matchedOption = options.find((option) => String(option.id) === String(id));

  if (matchedOption && preferPositionCode) {
    return getPositionDisplayLabel(matchedOption);
  }

  if (preferYears) {
    return (
      matchedOption?.years ||
      matchedOption?.label ||
      matchedOption?.name ||
      ""
    );
  }

  return matchedOption?.label || matchedOption?.name || matchedOption?.years || "";
};

const getOptionLabels = (options = [], ids = [], settings = {}) =>
  ids
    .map((id) => getOptionLabel(options, id, settings) || String(id))
    .filter(Boolean);

const getAdvancedParamChipLabel = (
  advancedParamLabels = {},
  paramId,
  fallbackLabel
) => advancedParamLabels?.[paramId] || fallbackLabel || `Parameter ${paramId}`;

export const buildActiveChips = (filters, optionMaps = {}, labels = CHIP_LABEL_DEFAULTS) => {
  const nextFilters = normalizeFilters(filters);
  const chips = [];

  getQuickFilterState(nextFilters, optionMaps.positions).forEach((label) => {
    chips.push({
      key: `qf:${label}`,
      label,
    });
  });

  const competitionSummary = getOptionLabels(
    optionMaps.competitions,
    nextFilters.selectedCompetition
  ).join(", ");

  if (competitionSummary) {
    chips.push({
      key: "selectedCompetition",
      label: `${labels.competition}: ${competitionSummary}`,
    });
  }

  const seasonSummary = getOptionLabels(
    optionMaps.seasons,
    nextFilters.selectedSeason,
    { preferYears: true }
  ).join(", ");

  if (seasonSummary) {
    chips.push({
      key: "selectedSeason",
      label: `${labels.season}: ${seasonSummary}`,
    });
  }

  const nationalitySummary = getOptionLabels(
    optionMaps.nations,
    nextFilters.selectedNation
  ).join(", ");

  if (nationalitySummary) {
    chips.push({
      key: "selectedNation",
      label: `${labels.nationality}: ${nationalitySummary}`,
    });
  }

  const positionSummary = getOptionLabels(
    optionMaps.positions,
    nextFilters.selectedPosition,
    { preferPositionCode: true }
  ).join(", ");

  if (positionSummary) {
    chips.push({
      key: "selectedPosition",
      label: `${labels.position || "Position"}: ${positionSummary}`,
    });
  }

  if (nextFilters.selectedLeg === "RL") {
    chips.push({
      key: "selectedLeg",
      label: `${labels.leg}: ${labels.both}`,
    });
  }

  if (nextFilters.selectedTeamType) {
    const teamTypeLabel = optionMaps.teamTypes?.[nextFilters.selectedTeamType] || nextFilters.selectedTeamType;

    chips.push({
      key: "selectedTeamType",
      label: `${labels.teamType}: ${teamTypeLabel}`,
    });
  }

  const ageValue = formatRangeValue(nextFilters.olderAge, nextFilters.underAge);
  if (ageValue) {
    chips.push({
      key: "age",
      label: `${labels.age}: ${ageValue}`,
    });
  }

  const heightValue = formatRangeValue(nextFilters.heightFrom, nextFilters.heightTo);
  if (heightValue) {
    chips.push({
      key: "height",
      label: `${labels.height}: ${heightValue}`,
    });
  }

  const metricRangeFields = [
    ["xg", "xgFrom", "xgTo"],
    ["npxg", "npxgFrom", "npxgTo"],
    ["xa", "xaFrom", "xaTo"],
    ["smrtIndexLeague", "smrtIndexLeagueFrom", "smrtIndexLeagueTo"],
    ["smrtIndexInternational", "smrtIndexInternationalFrom", "smrtIndexInternationalTo"],
    ["goals", "goalsFrom", "goalsTo"],
  ];

  metricRangeFields.forEach(([chipKey, fromKey, toKey]) => {
    const chipValue = formatRangeValue(nextFilters[fromKey], nextFilters[toKey]);

    if (!chipValue) {
      return;
    }

    chips.push({
      key: chipKey,
      label: `${labels[chipKey]}: ${chipValue}`,
    });
  });

  Object.entries(nextFilters.advancedParamRanges)
    .sort(([leftId], [rightId]) =>
      getAdvancedParamChipLabel(
        optionMaps.advancedParamLabels,
        leftId,
        `Parameter ${leftId}`
      ).localeCompare(
        getAdvancedParamChipLabel(
          optionMaps.advancedParamLabels,
          rightId,
          `Parameter ${rightId}`
        )
      )
    )
    .forEach(([paramId, range]) => {
      const chipValue = formatRangeValue(range.from, range.to);

      if (!chipValue) {
        return;
      }

      chips.push({
        key: `advparam:${paramId}`,
        label: `${getAdvancedParamChipLabel(
          optionMaps.advancedParamLabels,
          paramId
        )}: ${chipValue}`,
      });
    });

  return chips;
};

export const clearChip = (filters, chipKey, positionOptions = []) => {
  const nextFilters = normalizeFilters(filters);

  if (chipKey.startsWith("qf:")) {
    return toggleQuickFilter(nextFilters, chipKey.slice(3), positionOptions);
  }

  if (chipKey === "age") {
    return {
      ...nextFilters,
      olderAge: "",
      underAge: "",
    };
  }

  if (chipKey === "height") {
    return {
      ...nextFilters,
      heightFrom: "",
      heightTo: "",
    };
  }

  if (chipKey.startsWith("advparam:")) {
    const nextAdvancedParamRanges = {
      ...nextFilters.advancedParamRanges,
    };

    delete nextAdvancedParamRanges[chipKey.slice("advparam:".length)];

    return {
      ...nextFilters,
      advancedParamRanges: nextAdvancedParamRanges,
    };
  }

  const metricFieldMap = {
    xg: ["xgFrom", "xgTo"],
    npxg: ["npxgFrom", "npxgTo"],
    xa: ["xaFrom", "xaTo"],
    smrtIndexLeague: ["smrtIndexLeagueFrom", "smrtIndexLeagueTo"],
    smrtIndexInternational: [
      "smrtIndexInternationalFrom",
      "smrtIndexInternationalTo",
    ],
    goals: ["goalsFrom", "goalsTo"],
  };

  if (metricFieldMap[chipKey]) {
    const [fromKey, toKey] = metricFieldMap[chipKey];
    return {
      ...nextFilters,
      [fromKey]: "",
      [toKey]: "",
    };
  }

  if (ARRAY_FILTER_KEYS.has(chipKey)) {
    return {
      ...nextFilters,
      [chipKey]: [],
    };
  }

  return {
    ...nextFilters,
    [chipKey]: "",
  };
};

export const buildDynamicSearchRequestParams = (advancedParamRanges = {}) =>
  Object.entries(normalizeAdvancedParamRanges(advancedParamRanges)).reduce(
    (acc, [paramId, range]) => {
      if (range.from) {
        acc[`${ADVANCED_PARAM_QUERY_PREFIX}${paramId}_from`] = range.from;
      }

      if (range.to) {
        acc[`${ADVANCED_PARAM_QUERY_PREFIX}${paramId}_to`] = range.to;
      }

      if (hasRangeValue(range)) {
        acc[`${ADVANCED_PARAM_QUERY_PREFIX}${paramId}_mode`] =
          normalizeSearchStatMode(range.mode);
      }

      return acc;
    },
    {}
  );

export const getSelectedAdvancedParamIds = (advancedParamRanges = {}) =>
  Object.entries(normalizeAdvancedParamRanges(advancedParamRanges))
    .filter(([, range]) => hasRangeValue(range))
    .map(([paramId]) => String(paramId))
    .sort((leftId, rightId) => Number(leftId) - Number(rightId));

const getApiSortParam = (sort) => {
  const sortField = SORT_FIELD_BY_KEY[sort];

  if (!sortField) {
    return "";
  }

  return `-${sortField}_total`;
};

export const buildSearchRequestParams = ({
  filters,
  page,
  sort,
  pageSize = RESULT_PAGE_SIZE,
  additionalParamModes = {},
}) => {
  const nextFilters = normalizeFilters(filters);
  const requestedParamIds = Array.from(
    new Set([
      ...DEFAULT_RESULT_PARAM_IDS,
      ...getSelectedAdvancedParamIds(nextFilters.advancedParamRanges),
    ])
  );
  const additionalModeParams = Object.entries(additionalParamModes).reduce(
    (acc, [paramId, mode]) => {
      const normalizedParamId = Number(paramId);

      if (!Number.isInteger(normalizedParamId) || normalizedParamId <= 0) {
        return acc;
      }

      acc[`${ADVANCED_PARAM_QUERY_PREFIX}${normalizedParamId}_mode`] =
        normalizeSearchStatMode(mode);
      return acc;
    },
    {}
  );

  return {
    page,
    page_size: pageSize,
    sort: getApiSortParam(sort),
    param_id: requestedParamIds.join(","),
    year_season: nextFilters.selectedSeason.join(","),
    gender: nextFilters.selectedGender,
    age_from: nextFilters.olderAge,
    age_to: nextFilters.underAge,
    team_type: nextFilters.selectedTeamType,
    leg: nextFilters.selectedLeg,
    position: nextFilters.selectedPosition.join(","),
    country: nextFilters.selectedNation.join(","),
    competition: nextFilters.selectedCompetition.join(","),
    height_from: nextFilters.heightFrom,
    height_to: nextFilters.heightTo,
    xg_from: nextFilters.xgFrom,
    xg_to: nextFilters.xgTo,
    npxg_from: nextFilters.npxgFrom,
    npxg_to: nextFilters.npxgTo,
    xa_from: nextFilters.xaFrom,
    xa_to: nextFilters.xaTo,
    smrt_index_league_from: nextFilters.smrtIndexLeagueFrom,
    smrt_index_league_to: nextFilters.smrtIndexLeagueTo,
    smrt_index_international_from: nextFilters.smrtIndexInternationalFrom,
    smrt_index_international_to: nextFilters.smrtIndexInternationalTo,
    goals_from: nextFilters.goalsFrom,
    goals_to: nextFilters.goalsTo,
    ...buildDynamicSearchRequestParams(nextFilters.advancedParamRanges),
    ...additionalModeParams,
  };
};

export const filtersToQuery = ({
  filters,
  page,
  sort,
}) => {
  const nextFilters = normalizeFilters(filters);
  const params = new URLSearchParams();

  if (page && Number(page) > 1) {
    params.set("page", String(page));
  }

  if (sort && sort !== "smrt") {
    params.set("sort", sort);
  }

  VISIBLE_FILTER_KEYS.forEach((key) => {
    const value = nextFilters[key];

    if (ARRAY_FILTER_KEYS.has(key) && value.length > 0) {
      params.set(key, value.join(","));
      return;
    }

    if (!ARRAY_FILTER_KEYS.has(key) && value) {
      params.set(key, value);
    }
  });

  Object.entries(nextFilters.advancedParamRanges)
    .sort(([leftId], [rightId]) => Number(leftId) - Number(rightId))
    .forEach(([paramId, range]) => {
      if (range.from) {
        params.set(`${ADVANCED_PARAM_QUERY_PREFIX}${paramId}_from`, range.from);
      }

      if (range.to) {
        params.set(`${ADVANCED_PARAM_QUERY_PREFIX}${paramId}_to`, range.to);
      }

      if (hasRangeValue(range) || range.mode !== DEFAULT_STAT_MODE) {
        params.set(
          `${ADVANCED_PARAM_QUERY_PREFIX}${paramId}_mode`,
          normalizeSearchStatMode(range.mode)
        );
      }
    });

  return params.toString();
};

export const queryToSearchState = (search = "") => {
  const params = new URLSearchParams(search);
  const defaultState = getDefaultSearchState();
  const filters = { ...defaultState.filters };
  const legacyStatMode = normalizeSearchStatMode(params.get("statMode"));

  VISIBLE_FILTER_KEYS.forEach((key) => {
    const value = params.get(key);

    if (ARRAY_FILTER_KEYS.has(key)) {
      filters[key] = normalizeIdArray(value);
      return;
    }

    filters[key] = normalizeScalarValue(value);
  });

  params.forEach((value, key) => {
    const matchedDynamicRange = key.match(
      /^param_(\d+)_(from|to|mode)$/
    );

    if (!matchedDynamicRange || !value) {
      return;
    }

    const [, paramId, boundKey] = matchedDynamicRange;
    const normalizedParamId = Number(paramId);

    if (!Number.isInteger(normalizedParamId) || normalizedParamId <= 0) {
      return;
    }

    const nextRange = filters.advancedParamRanges[normalizedParamId] || {
      from: "",
      to: "",
    };

    filters.advancedParamRanges[normalizedParamId] = {
      ...nextRange,
      [boundKey]:
        boundKey === "mode"
          ? normalizeSearchStatMode(value)
          : normalizeScalarValue(value),
    };
  });

  filters.advancedParamRanges = normalizeAdvancedParamRanges(
    filters.advancedParamRanges,
    {
      legacyStatMode,
    }
  );

  const nextPage = Number(params.get("page")) || 1;
  const nextSort = params.get("sort") || defaultState.sort;

  return {
    filters,
    page: nextPage > 0 ? nextPage : 1,
    sort: normalizeSearchSortKey(nextSort),
    statMode: legacyStatMode,
  };
};

export const hasVisibleFilters = (filters) => {
  const nextFilters = normalizeFilters(filters);

  return (
    VISIBLE_FILTER_KEYS.some((key) => {
    const value = nextFilters[key];

    if (ARRAY_FILTER_KEYS.has(key)) {
      return value.length > 0;
    }

    return Boolean(value);
    }) || Object.values(nextFilters.advancedParamRanges).some(
      (range) => Boolean(range?.from || range?.to)
    )
  );
};

export const hasSearchScopeFilters = (filters) => {
  const nextFilters = normalizeFilters(filters);

  return (
    SEARCH_SCOPE_FILTER_KEYS.some((key) => {
      const value = nextFilters[key];

      if (ARRAY_FILTER_KEYS.has(key)) {
        return value.length > 0;
      }

      return Boolean(value);
    }) || Object.values(nextFilters.advancedParamRanges).some(
      (range) => Boolean(range?.from || range?.to)
    )
  );
};

export const prepareAdvancedParamCatalog = (
  parameters = [],
  resolveLabel = () => ""
) => {
  const uniqueParameters = parameters.reduce((acc, parameter) => {
    const parameterId = Number(parameter?.id ?? parameter?.parameter_id);

    if (!Number.isFinite(parameterId) || acc.has(parameterId)) {
      return acc;
    }

    const rawLabel = getAdvancedParamRawLabel(parameter);

    const resolvedLabel =
      resolveLabel(parameter) || getAdvancedParamFallbackLabel(parameter);
    const groupName =
      String(parameter?.group?.name || "").trim() || "Other";
    const sortOrder = Number(parameter?.sort_order);

    acc.set(parameterId, {
      id: parameterId,
      label: resolvedLabel,
      rawLabel,
      groupName,
      groupId: Number(parameter?.group?.id) || Number.MAX_SAFE_INTEGER,
      sortOrder: Number.isFinite(sortOrder) ? sortOrder : Number.MAX_SAFE_INTEGER,
      featuredKey: getAdvancedParamFeaturedKey({
        ...parameter,
        id: parameterId,
      }),
    });
    return acc;
  }, new Map());

  const items = Array.from(uniqueParameters.values()).sort((leftItem, rightItem) => {
    if (leftItem.label !== rightItem.label) {
      return leftItem.label.localeCompare(rightItem.label);
    }

    return leftItem.id - rightItem.id;
  });

  const groupsByName = items.reduce((acc, item) => {
    const existingGroup = acc.get(item.groupName) || {
      key: normalizeMetricLabel(item.groupName) || `group-${item.id}`,
      label: item.groupName,
      groupId: item.groupId,
      items: [],
    };

    existingGroup.items.push(item);
    acc.set(item.groupName, existingGroup);
    return acc;
  }, new Map());

  const groups = Array.from(groupsByName.values())
    .map((group) => ({
      ...group,
      items: [...group.items].sort((leftItem, rightItem) => {
        if (leftItem.sortOrder !== rightItem.sortOrder) {
          return leftItem.sortOrder - rightItem.sortOrder;
        }

        if (leftItem.label !== rightItem.label) {
          return leftItem.label.localeCompare(rightItem.label);
        }

        return leftItem.id - rightItem.id;
      }),
    }))
    .sort((leftGroup, rightGroup) => {
      if (leftGroup.groupId !== rightGroup.groupId) {
        return leftGroup.groupId - rightGroup.groupId;
      }

      return leftGroup.label.localeCompare(rightGroup.label);
    });

  return {
    items,
    itemsById: items.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {}),
    groups,
  };
};

const toNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : SAFE_NUMBER_FALLBACK;
};

const toOptionalNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const resolvePlayerAgeFromBirthDate = (birthDateValue) => {
  if (!birthDateValue) {
    return null;
  }

  const birthDate = new Date(birthDateValue);

  if (Number.isNaN(birthDate.getTime())) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age -= 1;
  }

  return age >= 0 ? age : null;
};

const resolvePlayerAge = (player = {}) => {
  const explicitAge = toOptionalNumber(player?.age);

  if (explicitAge !== null) {
    return explicitAge;
  }

  return resolvePlayerAgeFromBirthDate(
    player?.birth_date ||
      player?.birthDate ||
      player?.date_of_birth ||
      player?.dateOfBirth
  );
};

export const readParamValue = (param) => {
  if (!param) {
    return SAFE_NUMBER_FALLBACK;
  }

  if (typeof param.count === "number") {
    return toNumber(param.count);
  }

  if (!param.count || typeof param.count !== "object") {
    return SAFE_NUMBER_FALLBACK;
  }

  const keysInOrder = STAT_MODE_PRIORITY[DEFAULT_STAT_MODE];
  const matchedKey = keysInOrder.find((key) => param.count[key] !== undefined);

  return matchedKey ? toNumber(param.count[matchedKey]) : SAFE_NUMBER_FALLBACK;
};

export const readModeAwareParamValue = (
  param,
  statMode = DEFAULT_STAT_MODE
) => {
  if (!param) {
    return SAFE_NUMBER_FALLBACK;
  }

  if (typeof param.count === "number") {
    return toNumber(param.count);
  }

  if (!param.count || typeof param.count !== "object") {
    return SAFE_NUMBER_FALLBACK;
  }

  const normalizedStatMode = normalizeSearchStatMode(statMode);
  const directValue = toOptionalNumber(param.count[normalizedStatMode]);

  if (directValue !== null) {
    return directValue;
  }

  const keysInOrder = STAT_MODE_PRIORITY[normalizedStatMode];
  const matchedKey = keysInOrder.find(
    (key) => toOptionalNumber(param.count[key]) !== null
  );

  return matchedKey
    ? toOptionalNumber(param.count[matchedKey]) ?? SAFE_NUMBER_FALLBACK
    : SAFE_NUMBER_FALLBACK;
};

const getSeasonScore = (seasonLabel, seasonParams = []) => {
  const ids = seasonParams
    .map((param) => Number(param?.year_season_id || param?.season_id || 0))
    .filter((value) => Number.isFinite(value) && value > 0);

  if (ids.length > 0) {
    return Math.max(...ids);
  }

  const numericLabel = Number(String(seasonLabel).replace(/[^0-9]/g, ""));
  return Number.isFinite(numericLabel) ? numericLabel : SAFE_NUMBER_FALLBACK;
};

export const getLatestSeasonEntry = (paramsBySeason = {}) => {
  const entries = Object.entries(paramsBySeason).filter(([, value]) =>
    Array.isArray(value)
  );

  if (entries.length === 0) {
    return {
      seasonLabel: "",
      params: [],
    };
  }

  const [seasonLabel, params] = [...entries].sort((leftEntry, rightEntry) => {
    return getSeasonScore(rightEntry[0], rightEntry[1]) - getSeasonScore(leftEntry[0], leftEntry[1]);
  })[0];

  return {
    seasonLabel,
    params,
  };
};

const findParamById = (params = [], parameterId) =>
  params.find((param) => Number(param?.parameter_id) === Number(parameterId)) || null;

const findParamByLabel = (params = [], targetLabel) => {
  const normalizedTarget = normalizeMetricLabel(targetLabel);

  const exactMatch = params.find((param) => {
    return normalizeMetricLabel(param?.parameter_name) === normalizedTarget;
  });

  if (exactMatch) {
    return exactMatch;
  }

  return (
    params.find((param) => {
      const normalizedName = normalizeMetricLabel(param?.parameter_name);

      if (normalizedTarget === "xg" && normalizedName === "npxg") {
        return false;
      }

      return normalizedName.includes(normalizedTarget);
    }) || null
  );
};

export const getPlayerPositionCode = (positions = []) => {
  if (Array.isArray(positions)) {
    const matchedPosition = positions.find((position) =>
      Boolean(
        normalizePositionKey(
          getPositionDisplayLabel(position) || position?.lexic_id || ""
        )
      )
    );

    return normalizePositionKey(
      getPositionDisplayLabel(matchedPosition) ||
        matchedPosition?.lexic_id ||
        ""
    );
  }

  return normalizePositionKey(positions);
};

export const getPlayerInitials = (player = {}) => {
  const firstInitial = String(player?.name || "").trim().charAt(0);
  const secondInitial = String(player?.surname || "").trim().charAt(0);
  const initials = `${firstInitial}${secondInitial}`.trim();

  return initials || String(player?.name || "").trim().slice(0, 2).toUpperCase();
};

export const summarizeSearchResult = (player = {}, options = {}) => {
  const latestSeasonEntry = getLatestSeasonEntry(player.params);
  const latestParams = latestSeasonEntry.params;
  const normalizedParamRanges = normalizeAdvancedParamRanges(
    options.advancedParamRanges
  );
  const selectedStats = (options.selectedParamIds || []).map((paramId) => {
    const matchedParam = findParamById(latestParams, paramId);
    const selectedParamMode = normalizeSearchStatMode(
      normalizedParamRanges[String(paramId)]?.mode
    );

    return {
      id: Number(paramId),
      label:
        options.paramLabels?.[paramId] ||
        matchedParam?.parameter_name ||
        `Parameter ${paramId}`,
      value: matchedParam
        ? readModeAwareParamValue(matchedParam, selectedParamMode)
        : null,
    };
  });
  const nationalityValue = Array.isArray(player?.nationality)
    ? player.nationality.find((item) => item?.name)?.name || ""
    : "";
  const positionCode = getPlayerPositionCode(player?.position);
  const primaryPosition = Array.isArray(player?.position)
    ? player.position.find(
        (item) =>
          normalizePositionKey(
            getPositionDisplayLabel(item) || item?.lexic_id || ""
          ) === positionCode
      ) || player.position[0]
    : null;
  const goalsValue = readModeAwareParamValue(
    findParamById(latestParams, 2),
    DEFAULT_STAT_MODE
  );
  const assistsValue = readModeAwareParamValue(
    findParamById(latestParams, 201),
    DEFAULT_STAT_MODE
  );
  const xgValue = readModeAwareParamValue(
    findParamById(latestParams, 203) || findParamByLabel(latestParams, "xG"),
    DEFAULT_STAT_MODE
  );
  const smrtLeagueValue = readModeAwareParamValue(
    findParamById(latestParams, 206),
    DEFAULT_STAT_MODE
  );
  const smrtInternationalValue = readModeAwareParamValue(
    findParamById(latestParams, 207),
    DEFAULT_STAT_MODE
  );
  const primaryPositionCode =
    normalizeTrustedPositionCode(primaryPosition?.short_name) ||
    normalizeTrustedPositionCode(primaryPosition?.shortName) ||
    normalizeTrustedPositionCode(primaryPosition?.code);
  const playerPhoto =
    player?.photo ||
    player?.photos?.find((photo) => photo?.full)?.full ||
    player?.photos?.find((photo) => photo?.image)?.image ||
    null;

  return {
    id: player.id,
    fullName: player?.display_name || `${player?.name || ""} ${player?.surname || ""}`.trim(),
    name: player?.name || "",
    surname: player?.surname || "",
    known_name: player?.known_name || "",
    initials: getPlayerInitials(player),
    photo: playerPhoto,
    age: resolvePlayerAge(player),
    clubName: player?.team?.name || "",
    clubId: player?.team?.id || null,
    nationName: nationalityValue,
    positionCode,
    positionLabel:
      primaryPositionCode ||
      getPositionDisplayLabel(primaryPosition) ||
      positionCode,
    goals: goalsValue,
    assists: assistsValue,
    xg: xgValue,
    smrtIndex: smrtLeagueValue || smrtInternationalValue,
    selectedStats,
    seasonLabel: latestSeasonEntry.seasonLabel,
    raw: player,
  };
};

export const sortSummaryResults = (items = [], sort = "smrt") => {
  const valueGetter = {
    smrt: (item) => item.smrtIndex,
    goals: (item) => item.goals,
    assists: (item) => item.assists,
    xg: (item) => item.xg,
    age: (item) => item.age,
  }[sort] || ((item) => item.smrtIndex);

  return [...items].sort((leftItem, rightItem) => {
    const leftValue = valueGetter(leftItem);
    const rightValue = valueGetter(rightItem);

    if (sort === "age") {
      const leftHasAge = Number.isFinite(leftValue);
      const rightHasAge = Number.isFinite(rightValue);

      if (leftHasAge !== rightHasAge) {
        return leftHasAge ? -1 : 1;
      }

      if (leftValue !== rightValue) {
        return leftValue - rightValue;
      }
    } else if (leftValue !== rightValue) {
      return rightValue - leftValue;
    }

    return leftItem.fullName.localeCompare(rightItem.fullName);
  });
};

export const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 1) {
    return [];
  }

  const items = [];

  for (let page = 1; page <= totalPages; page += 1) {
    const isBoundaryPage = page === 1 || page === totalPages;
    const isNearCurrentPage = Math.abs(page - currentPage) <= 1;

    if (isBoundaryPage || isNearCurrentPage) {
      items.push(page);
      continue;
    }

    const previousItem = items[items.length - 1];
    if (previousItem !== "...") {
      items.push("...");
    }
  }

  return items;
};
